"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

/**
 * How far an image's aspect ratio may deviate from its frame before `fit="auto"`
 * gives up on cropping and letterboxes instead. 0.2 = 20%, which still lets a
 * 16:9 source fill a 16:10 frame (11% off — a barely noticeable trim) while
 * catching genuinely wrong shapes like tall full-page screenshots.
 */
const AUTO_FIT_TOLERANCE = 0.2;

/**
 * Rich image descriptor. Lets a surface carry intrinsic size, a blur-up
 * placeholder, and a reposition focal point alongside the URL.
 * Every field except `url` is optional so a bare string still works.
 */
export interface SmartImageSource {
  url: string;
  width?: number;
  height?: number;
  /** Tiny base64 preview for an instant blur-up (next/image `placeholder="blur"`). */
  blurDataURL?: string;
  /** 0–100 percentages; drives object-position (the "Reposition" value). */
  focalPoint?: { x: number; y: number };
}

export type SmartImageSrc = string | SmartImageSource | null | undefined;

export interface SmartImageProps {
  /** A bare URL string, or a rich {@link SmartImageSource}. */
  src: SmartImageSrc;
  alt: string;
  /**
   * Fixed aspect ratio for the frame, e.g. "16 / 10". Omit when the frame is
   * sized by `className` instead (e.g. a fixed-height banner).
   */
  aspectRatio?: string;
  /**
   * cover   = fill the frame, crop the overflow (banners, uniform grids).
   * contain = show the whole image, never crop; bars are filled by `backdrop`.
   * auto    = measure the loaded image and pick: `cover` when it already
   *           matches the frame (within AUTO_FIT_TOLERANCE) so it fills edge to
   *           edge with no visible crop, `contain` when it doesn't so nothing is
   *           cut off. Use for user-supplied images of unknown shape.
   */
  fit?: "cover" | "contain" | "auto";
  /** Override the focal point (0–100). Falls back to the source's, then center. */
  focalPoint?: { x: number; y: number };
  /**
   * How to fill the letterbox bars in `contain` mode.
   * Defaults to "blur" for contain and "none" for cover.
   */
  backdrop?: "blur" | "neutral" | "none";
  /** next/image responsive `sizes` hint. */
  sizes?: string;
  /** Eager-load (use for above-the-fold banners/heroes). */
  priority?: boolean;
  /** Classes for the FRAME wrapper (sizing, rounding, …). */
  className?: string;
  /** Extra classes for the foreground image element. */
  imgClassName?: string;
  /** Force-disable next/image optimization. */
  unoptimized?: boolean;
}

/** Accepts a string or object; returns a normalized source or null. */
function normalizeSource(src: SmartImageSrc): SmartImageSource | null {
  if (!src) return null;
  if (typeof src === "string") return src.trim() ? { url: src } : null;
  return src.url ? src : null;
}

/**
 * One image primitive for every cover/thumbnail/hero surface.
 *
 * Guarantees: never distorts (no `object-fit: fill`), never resizes the frame
 * per image, fills letterbox bars attractively, and degrades safely — https
 * URLs go through the next/image optimizer; data:/blob:/http URLs fall back to
 * a plain <img> so the optimizer is never handed something it can't load.
 */
export function SmartImage({
  src,
  alt,
  aspectRatio,
  fit = "cover",
  focalPoint,
  backdrop,
  sizes = "100vw",
  priority = false,
  className,
  imgClassName,
  unoptimized = false,
}: SmartImageProps) {
  // `auto` resolves once the image reports its intrinsic size (see onLoad below).
  const [autoFit, setAutoFit] = useState<"cover" | "contain" | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  const source = normalizeSource(src);
  if (!source) return null;

  const { url, blurDataURL } = source;
  const focal = focalPoint ?? source.focalPoint ?? { x: 50, y: 50 };
  const objectPosition = `${focal.x}% ${focal.y}%`;

  // Before measurement, assume `cover` — that's correct for every correctly
  // sized image, so the common case never changes its mind after load.
  const resolvedFit = fit === "auto" ? (autoFit ?? "cover") : fit;
  const isMeasuring = fit === "auto" && autoFit === null;
  const fitClass = resolvedFit === "contain" ? "object-contain" : "object-cover";
  const resolvedBackdrop = backdrop ?? (resolvedFit === "contain" ? "blur" : "none");

  /**
   * Compare the image's real aspect ratio against the frame it landed in and
   * decide whether cropping it would actually lose anything.
   */
  const measureFit = (naturalWidth: number, naturalHeight: number) => {
    if (fit !== "auto" || !naturalWidth || !naturalHeight) return;
    const frame = frameRef.current;
    if (!frame || !frame.clientWidth || !frame.clientHeight) {
      setAutoFit("cover");
      return;
    }
    const frameAspect = frame.clientWidth / frame.clientHeight;
    const imageAspect = naturalWidth / naturalHeight;
    const deviation = Math.abs(imageAspect - frameAspect) / frameAspect;
    setAutoFit(deviation > AUTO_FIT_TOLERANCE ? "contain" : "cover");
  };

  // The optimizer only handles https hosts allowlisted in next.config.
  // Anything else (data:, blob:, http:, relative) renders as a plain <img>.
  const isOptimizable = !unoptimized && /^https:\/\//.test(url);

  const renderLayer = (opts: {
    layerClassName: string;
    layerSizes: string;
    ariaHidden?: boolean;
    withPlaceholder?: boolean;
    /** Only the foreground layer drives the auto-fit measurement. */
    measures?: boolean;
  }) => {
    const onLoad = opts.measures
      ? (event: React.SyntheticEvent<HTMLImageElement>) =>
          measureFit(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)
      : undefined;

    if (isOptimizable) {
      return (
        <Image
          src={url}
          alt={opts.ariaHidden ? "" : alt}
          aria-hidden={opts.ariaHidden || undefined}
          fill
          sizes={opts.layerSizes}
          priority={priority}
          onLoad={onLoad}
          // Suppress the browser's native image drag (the translucent "ghost"
          // that follows the cursor) so only our pointer-based reposition runs.
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          className={cn("!m-0 max-w-none select-none [-webkit-user-drag:none]", opts.layerClassName)}
          style={{ objectPosition }}
          {...(opts.withPlaceholder && blurDataURL
            ? { placeholder: "blur" as const, blurDataURL }
            : {})}
        />
      );
    }
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={url}
        alt={opts.ariaHidden ? "" : alt}
        aria-hidden={opts.ariaHidden || undefined}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={onLoad}
        // Suppress the browser's native image drag (the translucent "ghost"
        // that follows the cursor) so only our pointer-based reposition runs.
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        className={cn("absolute inset-0 h-full w-full !m-0 max-w-none select-none [-webkit-user-drag:none]", opts.layerClassName)}
        style={{ objectPosition }}
      />
    );
  };

  return (
    <div
      ref={frameRef}
      // `not-prose` keeps editor `.prose` typography (which adds margin/max-width
      // to every descendant <img>) from offsetting/shrinking the cover image and
      // exposing the wrapper background as a gray strip.
      className={cn(
        "not-prose relative overflow-hidden bg-zinc-100 dark:bg-zinc-800",
        className,
      )}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {/* Letterbox fill for contain mode so empty bars look intentional. */}
      {resolvedFit === "contain" &&
        resolvedBackdrop !== "none" &&
        (resolvedBackdrop === "blur" ? (
          renderLayer({
            layerClassName: "object-cover scale-110 blur-2xl opacity-60",
            layerSizes: "32px",
            ariaHidden: true,
          })
        ) : (
          <div className="absolute inset-0 bg-zinc-100 dark:bg-zinc-800" />
        ))}

      {/* Foreground image. In `auto` mode it stays transparent for the one frame
          between load and measurement, so a letterboxed image never flashes
          cropped first. */}
      {renderLayer({
        layerClassName: cn(
          fitClass,
          fit === "auto" && "transition-opacity duration-200",
          isMeasuring && "opacity-0",
          imgClassName,
        ),
        layerSizes: sizes,
        withPlaceholder: true,
        measures: fit === "auto",
      })}
    </div>
  );
}

export default SmartImage;
